<template>
    <div data-test-view="sso">
        <l-headline
            :title="$t(`adminSettings.ssoHeadlineTitle`)"
            :sub-title="$t(`adminSettings.ssoHeadlineSubtitle`)"
        />

        <p>
            <l-text :text="$t(`adminSettings.ssoText.1`)" />"
            <a
                href="https://leapsome.zendesk.com/hc/en-us/articles/360004140874-Setting-up-single-sign-on-SSO-"
                target="_blank"
                rel="noopener noreferre"
            >
                {{ $t(`adminSettings.ssoText.2`) }}</a
            >
        </p>

        <br />

        <div>
            <label class="color-dark" for="integrations-saml-enabled-check">
                <input
                    id="integrations-saml-enabled-check"
                    v-model="integrations.saml.enabled"
                    type="checkbox"
                    data-test-id="team-settings-sso-enabled"
                />
                {{ $t(`adminSettings.ssoEnabledLabel`) }}
            </label>
        </div>

        <div class="tw-mt-2">
            <label class="color-dark" for="integrations-saml-enforced-check">
                <input
                    id="integrations-saml-enforced-check"
                    v-model="integrations.saml.enforced"
                    type="checkbox"
                    data-test-id="team-settings-sso-enforced"
                />
                {{ $t(`adminSettings.ssoEnforcedLabel`) }}

                <i
                    v-b-popover.hover.top="$t(`adminSettings.ssoEnforcedLabelHint`)"
                    class="fal fa-info-circle tw-text-red-400 tw-ml-2 tw-cursor-help"
                />
            </label>
        </div>

        <l-space :size="3" />

        <div class="row">
            <div class="col-md-12">
                <l-input-text
                    :label="$t(`adminSettings.ssoMetadataLabel`)"
                    :value="`https://www.leapsome.com/api/users/auth/saml/${team._id}/metadata`"
                    :readonly="true"
                    data-test-id="team-settings-sso-metadata"
                />
                <l-space :size="2" />
            </div>

            <div class="col-md-12">
                <l-input-text
                    :label="$t(`adminSettings.ssoEntityLabel`)"
                    :value="integrations.saml.entityIdOverride || 'https://www.leapsome.com'"
                    :readonly="true"
                    data-test-id="team-settings-sso-entity-id"
                />
                <l-space :size="2" />
            </div>

            <div class="col-md-12">
                <l-input-text
                    :label="$t(`adminSettings.ssoLoginLabel`)"
                    :value="`https://www.leapsome.com/api/users/auth/saml/${team._id}/login`"
                    :readonly="true"
                    data-test-id="team-settings-sso-login-url"
                />
                <l-space :size="2" />
            </div>

            <div class="col-md-12">
                <l-input-text
                    :label="$t(`adminSettings.ssoReplayLabel`)"
                    :value="`https://www.leapsome.com/api/users/auth/saml/${team._id}/assert`"
                    :readonly="true"
                    data-test-id="team-settings-sso-reply-url"
                />
                <l-space :size="2" />
            </div>
        </div>

        <div class="row">
            <div class="col-md-12">
                <l-input-text
                    ref="input-entry-point"
                    v-model="integrations.saml.entryPoint"
                    :label="$t(`adminSettings.ssoEntryPointLabel`)"
                    :placeholder="$t(`adminSettings.ssoEntryPointPlaceholder`)"
                    data-test-id="team-settings-sso-url"
                />
            </div>
        </div>

        <br />

        <div class="row">
            <div class="col-md-12">
                <l-input-text
                    ref="input-cert"
                    v-model="integrations.saml.cert"
                    type="password"
                    :label="$t(`adminSettings.ssoCertLabel`)"
                    :placeholder="$t(`adminSettings.ssoCertPlaceholder`)"
                    :error-message="$t(`adminSettings.ssoCertBase64Error`)"
                    data-test-id="team-settings-sso-cert"
                />
            </div>
        </div>

        <hr />

        <l-text-alert
            v-show="updateSuccess"
            type="success"
            :text="$t(`adminSettings.ssoUpdateSuccess`)"
            data-test-id="team-settings-sso-update-success"
        />

        <l-text-alert
            v-show="updateError"
            type="warning"
            :text="$t(`adminSettings.ssoUpdateError`)"
            data-test-id="team-settings-sso-update-error"
        />

        <div class="row">
            <div class="col-md-offset-8 col-md-4">
                <button
                    class="btn btn-primary btn-raised btn-block"
                    data-test-id="team-settings-sso-update"
                    @click="updateGlobalTeam()"
                >
                    {{ $t(`adminSettings.ssoUpdateBtn`) }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LSpace from "@/components/LSpace";
import LText from "@/components/LText";
import LTextAlert from "@/components/LTextAlert";

export default {
    components: {
        LHeadline,
        LInputText,
        LSpace,
        LText,
        LTextAlert,
    },
    data() {
        return {
            integrations: {
                saml: {},
            },
            updateSuccess: false,
            updateError: false,
        };
    },
    computed: {
        ...mapGetters({
            getTeamInfo: "teams/current/info",
            getTeamIntegrations: "teams/current/integrations",
        }),
        team() {
            return this.getTeamInfo();
        },
    },
    created() {
        // Cloning the information from the store so we can modify it before updating the store,
        // when the user clicks on the update button.
        //
        this.integrations = this.getTeamIntegrations();
    },
    methods: {
        isValid() {
            const inputs = Object.keys(this.$refs)
                .map(key => (key.startsWith("input-") ? this.$refs[key] : null))
                .filter(Boolean);

            return inputs.reduce((a, c) => c.validate() && a, true);
        },
        updateGlobalTeam() {
            this.updateSuccess = false;
            this.updateError = false;

            if (!this.isValid()) {
                return;
            }

            const formatCert = cert => {
                if (cert) {
                    return cert
                        .replace("-----BEGIN CERTIFICATE-----", "")
                        .replace("-----END CERTIFICATE-----", "");
                }
                return cert;
            };

            this.$store
                .dispatch({
                    type: "teams/current/update",
                    payload: {
                        scope: "saml",
                        update: {
                            enforced: this.integrations.saml.enforced,
                            enabled: this.integrations.saml.enabled,
                            entryPoint: this.integrations.saml.entryPoint,
                            logoutUrl: this.integrations.saml.logoutUrl,
                            cert: formatCert(this.integrations.saml.cert),
                        },
                    },
                })
                .then(() => {
                    this.updateSuccess = true;
                })
                .catch(() => {
                    this.updateError = true;
                });
        },
    },
};
</script>

<style scoped></style>
